import {
  Box,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import React from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { RiRouteLine } from "react-icons/ri";

interface PathConfigurationProps {
  path: string;
  description: string;
  isValidatingPath: boolean;
  pathAvailability: {
    checked: boolean;
    available: boolean;
    message?: string;
  };
  validationErrors: Record<string, string>;
  onPathChange: (path: string) => void;
  onDescriptionChange: (description: string) => void;
  pathInputRef: React.RefObject<HTMLInputElement | null>;
}

export const PathConfiguration: React.FC<PathConfigurationProps> = ({
  path,
  description,
  isValidatingPath,
  pathAvailability,
  validationErrors,
  onPathChange,
  onDescriptionChange,
  pathInputRef
}) => {
  return (
    <Box
      bg="white"
      rounded="xl"
      shadow="sm"
      border="1px"
      borderColor="gray.200"
      overflow="visible"
      position="relative"
    >
      <Box
        px={6}
        py={4}
        bg="gradient.to-r"
        bgGradient="linear(to-r, purple.50, blue.50)"
        borderBottom="1px"
        borderColor="gray.200"
      >
        <HStack gap={2}>
          <Icon as={RiRouteLine} boxSize={5} color="purple.600" />
          <Text fontWeight="semibold" fontSize="lg" color="gray.800">
            Path Configuration
          </Text>
        </HStack>
      </Box>

      <Box p={6} position="relative" overflow="visible">
        <VStack gap={4} align="stretch" overflow="visible">
          {/* Path Input with validation */}
          <Box>
            <HStack mb={2} justify="space-between">
              <Text fontSize="sm" fontWeight="medium">
                Path URL <Text as="span" color="red.500">*</Text>
              </Text>
              {isValidatingPath && (
                <HStack gap={1}>
                  <Spinner size="xs" color="blue.500" />
                  <Text fontSize="xs" color="gray.500">Checking availability...</Text>
                </HStack>
              )}
              {!isValidatingPath && pathAvailability.checked && (
                <HStack gap={1}>
                  <Icon 
                    as={pathAvailability.available ? FiCheck : FiX} 
                    color={pathAvailability.available ? "green.500" : "red.500"}
                    boxSize={4}
                  />
                  <Text 
                    fontSize="xs" 
                    color={pathAvailability.available ? "green.600" : "red.600"}
                    fontWeight="medium"
                  >
                    {pathAvailability.available ? "Path available" : pathAvailability.message || "Path not available"}
                  </Text>
                </HStack>
              )}
            </HStack>
            <Input
              ref={pathInputRef}
              placeholder="/example/path"
              value={path}
              onChange={(e) => onPathChange(e.target.value)}
              _invalid={validationErrors.path ? { 
                borderColor: "red.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-red-500)"
              } : undefined}
              size="lg"
              _focus={{
                borderColor: validationErrors.path ? "red.500" : "blue.500",
                boxShadow: validationErrors.path 
                  ? "0 0 0 1px var(--chakra-colors-red-500)"
                  : "0 0 0 1px var(--chakra-colors-blue-500)"
              }}
            />
            {validationErrors.path && !isValidatingPath && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {validationErrors.path}
              </Text>
            )}
            <Text fontSize="xs" color="gray.500" mt={1}>
              The URL path where this content will be accessible
            </Text>
          </Box>

          {/* Description */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Description <Text as="span" color="gray.500" fontSize="xs">(Optional)</Text>
            </Text>
            <Textarea
              placeholder="Describe the purpose of this path..."
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              rows={3}
              resize="vertical"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              Add a description to help identify this path's purpose
            </Text>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};